import { symphonySessionContext, type SymphonyIdentify, type SymphonySessionContext } from "@/symphony/contract";

export function requestUrl(input: RequestInfo | URL): string | null {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.toString();
  if (typeof Request !== "undefined" && input instanceof Request) return input.url;
  return null;
}

export function isSymphonySessionRequest(url: string, gateway: string): boolean {
  try {
    const target = new URL(url, gateway);
    const gate = new URL(gateway);
    const path = target.pathname.replace(/\/$/, "") || "/";
    return target.origin === gate.origin && path === "/v1/session";
  } catch {
    return false;
  }
}

export function withSupabaseAccessToken(body: string, token: string | null): string {
  if (!token) return body;
  let parsed: unknown;
  try {
    parsed = JSON.parse(body);
  } catch {
    return body;
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return body;
  return JSON.stringify({ ...parsed, supabaseAccessToken: token });
}

export function installSymphonySessionToken(gateway: string, getAccessToken: () => string | null): () => void {
  const original = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const url = requestUrl(input);
    const method = (init?.method ?? (input instanceof Request ? input.method : "GET")).toUpperCase();
    if (url && method === "POST" && isSymphonySessionRequest(url, gateway) && typeof init?.body === "string") {
      const headers = new Headers(init.headers);
      headers.set("content-type", "application/json");
      const response = await original(url, {
        ...init,
        method: "POST",
        headers,
        body: withSupabaseAccessToken(init.body, getAccessToken()),
      });
      void response.clone().json().then(() => {
        document.documentElement.dataset.speakSymphonySession = response.ok ? "ok" : "rejected";
      }).catch(() => {
        document.documentElement.dataset.speakSymphonySession = response.ok ? "ok" : "rejected";
      });
      return response;
    }
    return original(input, init);
  };
  return () => {
    window.fetch = original;
  };
}

export interface SymphonyClientApi {
  open?: () => void;
  close?: () => void;
  identify?: (identity: SymphonyIdentify) => void;
  setContext?: (context: SymphonySessionContext | Record<string, never>) => void;
  registerClientTool?: (name: string, schema: Record<string, unknown>, handler: (args: unknown) => unknown) => void;
}

export function readSymphonyClient(): SymphonyClientApi | null {
  const client = (window as Window & { Symphony?: SymphonyClientApi }).Symphony;
  return client ?? null;
}

export function pushSymphonySession(session: {
  access_token: string;
  expires_at?: number | null;
  user: { id: string };
} | null): void {
  const client = readSymphonyClient();
  if (!client) return;
  if (!session) {
    client.setContext?.({});
    client.close?.();
    return;
  }
  client.identify?.({ id: session.user.id });
  client.setContext?.(symphonySessionContext(session));
}

export function mountSymphonyLoader(options: {
  publishableKey: string;
  loaderUrl: string;
  gateway: string;
  getAccessToken: () => string | null;
  onReady?: () => void;
}): () => void {
  const restoreFetch = installSymphonySessionToken(options.gateway, options.getAccessToken);
  const before = new Set(document.querySelectorAll("body > div[data-symphony]"));
  const script = document.createElement("script");
  script.src = options.loaderUrl;
  script.async = false;
  script.dataset.speakSymphonyLoader = "1";
  script.setAttribute("data-symphony", options.publishableKey);
  script.setAttribute("data-gateway", options.gateway);
  script.addEventListener("load", () => {
    document.documentElement.dataset.speakSymphony = "loaded";
    options.onReady?.();
  });
  script.addEventListener("error", () => {
    document.documentElement.dataset.speakSymphony = "error";
  });
  document.documentElement.dataset.speakSymphony = "loading";
  document.head.appendChild(script);

  return () => {
    restoreFetch();
    script.remove();
    document.querySelectorAll("body > div[data-symphony]").forEach((node) => {
      if (!before.has(node)) node.remove();
    });
    delete document.documentElement.dataset.speakSymphony;
    delete document.documentElement.dataset.speakSymphonySession;
    pushSymphonySession(null);
  };
}
